import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { SeriesService, SeriesMasterDto } from '../../services/series';
import { DropdownService, DropdownItem } from '../../services/dropdown';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

@Component({
  selector: 'app-series-master',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    TableModule,
    TooltipModule,
    TagModule,
    InputTextModule,
  ],
  templateUrl: './series-master.html',
  styleUrls: ['./series-master.css'],
})
export class SeriesMaster implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(SeriesService);
  private dropdown = inject(DropdownService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  /** Page heading — pulled from the menu so it always matches the clicked item. */
  readonly pageTitle = inject(PageTitleService).title;

  readonly resetTypes = ['None', 'Daily', 'Monthly', 'Yearly'];

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  masters = signal<any[]>([]);
  types = signal<DropdownItem[]>([]);

  form: FormGroup = this.fb.group({
    id: [0],
    seriesTypeID: [null, Validators.required],
    seriesName: ['', Validators.required],
    separator: ['-'],
    resetType: ['None'],
  });

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.load();
      this.loadTypes();
    }
  }

  load() {
    this.loading.set(true);
    this.svc.getAllMasters().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.masters.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  loadTypes() {
    this.dropdown.seriesType().subscribe((opts) => this.types.set(opts));
  }

  typeNameById(id: number) {
    const t = this.types().find((x) => +x.value === id);
    return t ? t.label : `#${id}`;
  }

  openAdd() {
    this.editing.set(false);
    this.form.reset({ id: 0, seriesTypeID: null, seriesName: '', separator: '-', resetType: 'None' });
    this.showModal.set(true);
  }

  openEdit(m: any) {
    this.editing.set(true);
    this.form.reset({
      id: m.id,
      seriesTypeID: m.seriesTypeID,
      seriesName: m.seriesName ?? '',
      separator: m.separator ?? '',
      resetType: m.resetType ?? 'None',
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Series type and name are required');
      return;
    }
    this.saving.set(true);
    const dto = { ...this.form.value, seriesTypeID: Number(this.form.value.seriesTypeID) } as SeriesMasterDto;
    const req = this.editing() ? this.svc.updateMaster(dto) : this.svc.insertMaster(dto);

    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save series');
          return;
        }
        this.toast.success(this.editing() ? 'Series updated' : 'Series created');
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  async remove(m: any) {
    const ok = await this.toast.confirm(`"${m.seriesName}" will be deleted.`, 'Delete series?', 'Delete');
    if (!ok) return;
    this.svc.deleteMaster(m.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Series deleted');
        this.load();
      },
    });
  }
}
